import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Loader2, ArrowRight, CheckCircle, Mail, KeyRound } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ResetPassword = () => {
    const [step, setStep] = useState('otp'); // 'otp' or 'new_password'
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();
    
    // Auth Check
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const emailFromUrl = queryParams.get('email');
        
        if (emailFromUrl) {
            setEmail(emailFromUrl);
        }

        // Check if they somehow have a session (e.g., clicked an old magic link)
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) {
                setStep('new_password');
            } else if (!emailFromUrl) {
                // No session and no email provided => nothing we can do
                navigate('/login');
            }
        });
    }, [location, navigate]);

    // OTP Inputs Refs
    const inputRefs = [
        useRef(null), useRef(null), useRef(null),
        useRef(null), useRef(null), useRef(null)
    ];

    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        // Auto move next
        if (value && index < 5) {
            inputRefs[index + 1].current?.focus();
        }

        // Auto verify if 6 digits
        if (value && index === 5) {
            const fullToken = [...newOtp].join('');
            if (fullToken.length === 6) {
                verifyExplicitOtp(fullToken);
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '');
        if (pastedData) {
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length; i++) {
                if (i < 6) newOtp[i] = pastedData[i];
            }
            setOtp(newOtp);
            if (pastedData.length === 6) {
                verifyExplicitOtp(pastedData);
            } else {
                inputRefs[pastedData.length < 6 ? pastedData.length : 5].current?.focus();
            }
        }
    };

    const verifyExplicitOtp = async (tokenStr) => {
        setLoading(true);
        setError(null);

        try {
            const { error: verifyError } = await supabase.auth.verifyOtp({
                email,
                token: tokenStr,
                type: 'recovery', // Specifically for password recovery tokens
            });

            if (verifyError) throw verifyError;

            // Success => OTP valid, session generated successfully!
            setStep('new_password');
        } catch (err) {
            console.error('Error verifying OTP:', err);
            setError('Ogiltig eller utgången kod. Försök igen.');
            setOtp(['', '', '', '', '', '']);
            inputRefs[0].current?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            setError('Lösenordet måste vara minst 6 tecken.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { error: updateError } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (updateError) throw updateError;

            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 2500);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-slate-900 shadow-2xl rounded-3xl p-8 max-w-sm w-full border border-slate-800 text-center"
                >
                    <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-500/20">
                        <CheckCircle className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-2">Lösenordet har uppdaterats</h1>
                    <p className="text-slate-400 mb-6 text-sm">Du omdirigeras nu tillbaka till inloggningssidan.</p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-sans">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-900 shadow-2xl rounded-3xl p-8 max-w-md w-full border border-slate-800"
            >
                {step === 'otp' ? (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <div className="text-center mb-8">
                            <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-indigo-500/20">
                                <KeyRound className="w-7 h-7 text-indigo-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2 font-logo">
                                Ange din kod
                            </h1>
                            <p className="text-slate-400 text-sm">
                                Vi har skickat en 6-siffrig kod till <br />
                                <span className="text-white font-medium">{email}</span>
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm text-center">
                                {error}
                            </div>
                        )}

                        <div 
                            className="flex justify-center gap-2 sm:gap-3 mb-8"
                            onPaste={handlePaste}
                        >
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={inputRefs[index]}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="\d{1}"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                    className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-slate-800 border-2 border-slate-700 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-slate-800 transition-all text-white placeholder-slate-600 shadow-inner"
                                    disabled={loading}
                                />
                            ))}
                        </div>

                        <div className="text-center">
                            {loading && (
                                <div className="flex items-center justify-center text-slate-400 text-sm font-medium">
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Verifierar kod...
                                </div>
                            )}
                            {!loading && (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="text-sm text-slate-500 hover:text-white transition-colors"
                                >
                                    Avbryt och återgå till inloggning
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <div className="text-center mb-8">
                            <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-emerald-500/20">
                                <Lock className="w-7 h-7 text-emerald-400" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2 font-logo">
                                Nytt lösenord
                            </h1>
                            <p className="text-slate-400 text-sm">
                                Välj ett starkt lösenord för ditt konto.
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm text-center">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleUpdatePassword} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-2">Nytt lösenord</label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full pl-11 pr-4 py-3.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                                        placeholder="Minst 6 tecken"
                                        minLength={6}
                                        required
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center group shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        Spara nytt lösenord <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default ResetPassword;
